import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { api } from '../utils/api';
import { Container, Typography, Paper, Box, CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, List, ListItem, ListItemText, Divider, IconButton, Button } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const SessionReflectionsPage = () => {
  const { sessionId } = useParams();
  const [reflectionsData, setReflectionsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItemVotes, setSelectedItemVotes] = useState(null);

  const fetchReflectionsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/sessions/${sessionId}/reflections`);
      setReflectionsData(response.data);
    } catch (err) {
      console.error('Error fetching reflections data:', err);
      setError(err.response?.data?.message || `Failed to load reflections data for session ${sessionId}.`);
    }
    setLoading(false);
  }, [sessionId]);

  useEffect(() => {
    fetchReflectionsData();
  }, [fetchReflectionsData]);

  const handleItemRowClick = (item) => {
    setSelectedItemVotes(item);
  };

  if (loading) {
    return (
      <Container sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!reflectionsData) {
    return (
      <Container sx={{ py: 3 }}>
        <Alert severity="info">No reflections data found for this session.</Alert>
      </Container>
    );
  }

  const { sessionName, sessionDate, cardDeckType, participants, estimatedItems } = reflectionsData;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <IconButton component={RouterLink} to="/dashboard" aria-label="back to dashboard" sx={{ mb: 2 }}>
        <ArrowBackIcon />
      </IconButton>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Session Reflections: {sessionName}
        </Typography>
        <Box sx={{ mb: 2, display: 'flex', gap: 3 }}>
          <Typography variant="subtitle1">
            Date: {new Date(sessionDate).toLocaleDateString()}
          </Typography>
          <Typography variant="subtitle1">
            Card Deck: {cardDeckType}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h5" gutterBottom>
          Participants ({participants?.length || 0})
        </Typography>
        {participants && participants.length > 0 ? (
          <List dense>
            {participants.map((p) => (
              <ListItem key={p.id || p.userId || p.name}>
                <ListItemText primary={p.name} secondary={p.role} />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography>No participant information available.</Typography>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="h5" gutterBottom>
          Estimated Items Summary ({estimatedItems?.length || 0})
        </Typography>
        {estimatedItems && estimatedItems.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table sx={{ minWidth: 650 }} aria-label="estimated items table">
              <TableHead>
                <TableRow>
                  <TableCell>Item Title</TableCell>
                  <TableCell align="right">Final Estimate</TableCell>
                  <TableCell align="center"># Voters</TableCell>
                  <TableCell align="right">Agreement (%)</TableCell>
                  <TableCell align="center">View Votes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {estimatedItems.map((item) => (
                  <TableRow 
                    key={item.itemId} 
                    hover 
                    onClick={() => handleItemRowClick(item)}
                    sx={{ cursor: 'pointer'}}
                  >
                    <TableCell component="th" scope="row">
                      {item.itemTitle}
                    </TableCell>
                    <TableCell align="right">{item.finalEstimate || 'N/A'}</TableCell>
                    <TableCell align="center">{item.numberOfVoters}</TableCell>
                    <TableCell align="right">{item.agreementPercentage?.toFixed(1) || 'N/A'}%</TableCell>
                    <TableCell align="center">
                      <Button size="small" onClick={(e) => { e.stopPropagation(); handleItemRowClick(item);}}>
                        Chart
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography>No items were estimated in this session.</Typography>
        )}

        {selectedItemVotes && (
          <Box sx={{ mt: 4, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Vote Distribution for: {selectedItemVotes.itemTitle}
            </Typography>
            {Object.keys(selectedItemVotes.voteDistribution).length > 0 ? (
               <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                    data={Object.entries(selectedItemVotes.voteDistribution).map(([name, value]) => ({ name, count: value }))}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#8884d8" name="Votes" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
                <Typography sx={{textAlign: 'center', py: 2}}>No votes recorded for this item.</Typography>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default SessionReflectionsPage; 